import { useRef, useState } from 'react'
import { blocksToMd, type Block, type Blocks } from '@w/content-blocks'
import { useEditorState, type AnswerPractice, type BlockEditorHandle } from '@w/content-blocks-editor'
import { AudioOptionsProvider, AudioRecorder } from '@w/react-audio'
import { IconButton } from '@w/ui/components/icon-button'
import { ChevronRightIcon, EyeIcon, SendIcon } from '@w/ui/components/icon'
import { unknownErrorToError, type UFile } from '@w/utils'
import { useTranslation } from '../i18n'
import { useRouterContext } from '../router/flashcards-fe.router-bindings'
import type { RuleBlock } from '../lexicon/lexicon-model'
import { ruleCardBlocks, rulePracticeItems, rulePracticeQid } from '../lexicon/rule-blocks'
import { groupPracticeQid, groupPracticeQuestions, shownPracticeQuestion } from './question-group-blocks'
import { answerViewerState, gradeQuestion, graders, transcribeAnswer } from '../training/grade-question'
import { trainingControls, type TrainingLevel } from '../training/training-controls'
import {
  canSubmitTrainingBlock,
  describeTrainingAnswer,
  getTrainingExcerptBlocks,
  getTrainingParts,
  shuffledOptionOrder,
  type ActiveTrainingBlock,
} from '../training/training-scoring'

/**
 * Practice on the page (flashcards-training.spec.md "Practice on the page"): every question on the page in reader
 * and translated views renders, is submitted and graded as in training, with training's controls minus rating, inline
 * right after the question. Nothing is recorded: a reveal only shows the answer.
 * Marks and the rule practice cycle live here and reach the editor as `answerPractice`, never the page document.
 * A question is its qid, as a training card is: inside a rule each recall cloze has its own, and the `.practice` clozes
 * share one (flashcards-training.requirements.md 2026-11-02), of which one item shows at a time.
 */
type QuestionPractice =
  | { status: 'grading' }
  | { status: 'failed'; error: unknown }
  | { status: 'shown'; marks: Record<string, number | null>; mark: number | null; feedback: string | null }

/** `advanced`: per practice qid, how many practice items this visit moved past training's next one (never written). */
type PracticeState = { key: string; questions: Record<string, QuestionPractice>; advanced: Record<string, number> }
type CardLevels = { qid: string; importance: TrainingLevel; difficulty: TrainingLevel; reviewCount: number }
type Controls = { primary: 'voice' | 'reveal'; reveal: boolean; voice: boolean } | null

export function usePagePractice({ key, cards, enabled }: {
  key: string
  /** The learner's cards on the page: their levels pick the reveal and voice actions, as in training. */
  cards: readonly CardLevels[]
  enabled: boolean
}): {
  answerPractice?: AnswerPractice
  attach: (handle: BlockEditorHandle) => void
} {
  const routerCtx = useRouterContext()
  const handleRef = useRef<BlockEditorHandle | null>(null)
  const [state, setState] = useState<PracticeState>({ key, questions: {}, advanced: {} })
  // Each choice keeps one shuffle per page view, as a training card keeps its shuffle.
  const [orders] = useState(() => new Map<string, number[]>())
  // A new page or view starts unanswered; a late grade for an old view is dropped below.
  if (state.key !== key) setState({ key, questions: {}, advanced: {} })
  const questions = state.key === key ? state.questions : {}
  const advanced = state.key === key ? state.advanced : {}
  // A rule's or question group's practice item is the one training shows next: the card's reviewCount picks it.
  const reviews = (qid: string) => cards.find(card => card.qid === qid)?.reviewCount ?? 0

  function settle(question: string, next: QuestionPractice | null) {
    setState(current => {
      if (current.key !== key) return current
      const { [question]: _previous, ...rest } = current.questions
      return { ...current, questions: next ? { ...rest, [question]: next } : rest }
    })
  }

  /** Of each rule with authored practice items, the items other than the one training would show at this repetition. */
  function hidden(blocks: readonly Block[]): ReadonlySet<string> {
    const ids = new Set<string>()
    for (const block of blocks) {
      if (block.type === 'questionGroup') {
        const qid = groupPracticeQid(block)
        const shown = qid ? shownPracticeQuestion(block, reviews(qid) + (advanced[qid] ?? 0)) : undefined
        for (const item of groupPracticeQuestions(block)) if (item.id !== shown?.id) ids.add(item.id)
        continue
      }
      const practice = block.type === 'rule' && !block.affix ? rulePracticeQid(block.children) : undefined
      if (block.type !== 'rule' || !practice) continue
      const items = rulePracticeItems(block.children)
      const shown = new Set(rulePracticeItems(ruleCardBlocks(block as RuleBlock, [], reviews(practice) + (advanced[practice] ?? 0))).map(item => item.id))
      for (const item of items) if (!shown.has(item.id)) ids.add(item.id)
    }
    return ids
  }

  /** Where the shown practice item of `qid` sits among its rule's or group's practice items (0-based), and how many there are. */
  function positionOf(qid: string): { index: number; count: number } | undefined {
    const blocks = handleRef.current?.getSnapshot().blocks ?? []
    const rep = reviews(qid) + (advanced[qid] ?? 0)
    for (const block of blocks) {
      if (block.type === 'questionGroup' && groupPracticeQid(block) === qid) {
        const items = groupPracticeQuestions(block)
        const shown = shownPracticeQuestion(block, rep)
        return { index: items.findIndex(item => item.id === shown?.id), count: items.length }
      }
      if (block.type === 'rule' && !block.affix && rulePracticeQid(block.children) === qid) {
        const items = rulePracticeItems(block.children)
        const shown = new Set(rulePracticeItems(ruleCardBlocks(block as RuleBlock, [], rep)).map(item => item.id))
        return { index: items.findIndex(item => shown.has(item.id)), count: items.length }
      }
    }
    return undefined
  }

  function controlsOf(parts: readonly ActiveTrainingBlock[]): Controls {
    const prompt = parts.at(0)
    const kind = prompt?.type === 'cloze' ? 'cloze' : prompt?.type === 'question' && prompt.subtype === 'la' ? 'la' : null
    if (!prompt || !kind) return null
    const card = cards.find(candidate => candidate.qid === prompt.qid)
    const controls = trainingControls(kind, card?.difficulty ?? 'medium', card?.importance ?? 'medium')
    // One voice transcript cannot fill several parts.
    return parts.length > 1 ? { ...controls, voice: false } : controls
  }

  function partsOf(question: string): { parts: ActiveTrainingBlock[]; blocks: Blocks } | null {
    const handle = handleRef.current
    if (!handle) return null
    const current = withoutBlocks(handle.getSnapshot().blocks, hidden(handle.getSnapshot().blocks))
    const parts = getTrainingParts(current, question)
    return parts.length > 0 ? { parts, blocks: current } : null
  }

  function reveal(question: string, mark: number | null) {
    const found = partsOf(question)
    if (!found) return
    settle(question, { status: 'shown', marks: Object.fromEntries(found.parts.map(part => [part.id, mark])), mark, feedback: null })
  }

  async function submit(question: string, trigger: 'enter' | 'pick' | 'button', recording?: UFile) {
    const found = partsOf(question)
    const handle = handleRef.current
    const current = questions[question]
    if (!found || !handle || current?.status === 'grading' || current?.status === 'shown') return
    const { parts } = found
    const prompt = parts[0]
    if (prompt.type === 'question' && prompt.subtype === 'sr') return
    // As in training, a pick submits only a single-choice question with one part.
    if (trigger === 'pick' && parts.length > 1) return
    const answers = handle.getAnswers()
    const states = parts.map(part => answerViewerState(answers[part.id] ?? {}))
    if (!recording && !parts.some((part, index) => canSubmitTrainingBlock(part, states[index]))) {
      // Enter on an empty answer reveals, as training's Enter does where its card levels offer a reveal.
      if (trigger === 'enter' && controlsOf(parts)?.reveal) reveal(question, null)
      return
    }
    settle(question, { status: 'grading' })
    try {
      const grade = await routerCtx.tel.span({ name: 'page.practice.submit', target: 'job', input: { qid: prompt.qid, question } }, async (tel) => {
        if (recording) {
          const text = await transcribeAnswer(routerCtx.rpcClient, recording, tel)
          states[0] = { ...states[0], textValue: text }
          handle.setAnswer(prompt.id, { text, audio: recording })
        }
        return gradeQuestion({ parts, states, excerpt: getTrainingExcerptBlocks(found.blocks, prompt.qid), graders: graders(routerCtx.rpcClient), tel })
      })
      settle(question, { status: 'shown', marks: Object.fromEntries(parts.map((part, index) => [part.id, grade.partMarks[index] ?? grade.score])), mark: grade.score, feedback: grade.feedback })
    } catch (error) {
      // As in training: the question stays unanswered so the learner can retry; a failed pick is cleared.
      if (prompt.type === 'question' && prompt.subtype === 'sc') handle.setAnswer(prompt.id, {})
      settle(question, { status: 'failed', error })
    }
  }

  function recordedSpoken(question: string, audio: UFile) {
    const found = partsOf(question)
    if (!found) return
    handleRef.current?.setAnswer(found.parts[0].id, { audio })
    reveal(question, null)
  }

  /** The next practice item of the rule in the same cycle, unanswered; the one left keeps no typed answer. */
  function next(qid: string) {
    for (const part of partsOf(qid)?.parts ?? []) handleRef.current?.setAnswer(part.id, {})
    setState(current => {
      if (current.key !== key) return current
      const { [qid]: _answered, ...rest } = current.questions
      return { ...current, questions: rest, advanced: { ...current.advanced, [qid]: (current.advanced[qid] ?? 0) + 1 } }
    })
  }

  const attach = (handle: BlockEditorHandle) => { handleRef.current = handle }
  if (!enabled) return { attach }
  const random = routerCtx.testSupport?.trainingOptionRandom
  const marks = Object.assign({}, ...Object.values(questions).map(question => question.status === 'shown' ? question.marks : {})) as Record<string, number | null>
  return {
    attach,
    answerPractice: {
      marks,
      hidden,
      // Keyed by what the learner sees, not block ids: the translated view's document has its own ids.
      optionOrder: choice => {
        const orderKey = JSON.stringify([key, choice.qid, choice.opts.map(option => blocksToMd(option))])
        const order = orders.get(orderKey) ?? shuffledOptionOrder(choice.opts.length, random)
        orders.set(orderKey, order)
        return order
      },
      submit: (question, trigger) => { void submit(question, trigger) },
      controls: (question, rendered) => {
        const parts = getTrainingParts(rendered, question)
        return <AudioOptionsProvider value={routerCtx.testSupport?.audioOptions}>
          <PracticeControls parts={parts} controls={controlsOf(parts)} practice={questions[question]}
            onSubmit={() => { void submit(question, 'button') }} onReveal={() => { reveal(question, null) }}
            onNext={parts.some(part => part.practice === true) ? () => { next(question) } : undefined}
            position={parts.some(part => part.practice === true) ? positionOf(question) : undefined}
            onVoice={audio => { void submit(question, 'button', audio) }} onRecorded={audio => { recordedSpoken(question, audio) }} />
        </AudioOptionsProvider>
      },
    },
  }
}

function withoutBlocks(blocks: Blocks, ids: ReadonlySet<string>): Blocks {
  if (ids.size === 0) return blocks
  return blocks.flatMap((block): Blocks => ids.has(block.id) ? []
    : 'children' in block && Array.isArray(block.children) ? [{ ...block, children: withoutBlocks(block.children as Blocks, ids) } as Block] : [block])
}

function PracticeControls({ parts, controls, practice, onSubmit, onReveal, onNext, position, onVoice, onRecorded }: {
  parts: ActiveTrainingBlock[]
  /** Training's reveal and voice actions for cloze and long answer; null for other questions. */
  controls: Controls
  practice?: QuestionPractice
  onSubmit: () => void
  onReveal: () => void
  /** A rule's practice question: after its answer shows, Next moves to the rule's next practice item. */
  onNext?: () => void
  /** The shown practice item's place among its rule's or group's items, read as "1/2". */
  position?: { index: number; count: number }
  onVoice: (audio: UFile) => void
  onRecorded: (audio: UFile) => void
}) {
  const { t } = useTranslation('training')
  const answers = useEditorState(state => state.answers)
  const prompt = parts.at(0)
  if (!prompt) return null
  const spoken = prompt.type === 'question' && prompt.subtype === 'sr'
  const singleChoice = parts.length === 1 && prompt.type === 'question' && prompt.subtype === 'sc'
  const hasAnswer = parts.some(part => canSubmitTrainingBlock(part, answerViewerState(answers[part.id] ?? {})))
  const grading = practice?.status === 'grading'
  const actions = controls ? (controls.primary === 'voice' ? ['voice', 'reveal'] as const : ['reveal', 'voice'] as const) : []
  const shown = practice?.status === 'shown'
  const feedback = shown && practice.feedback ? <span data-testid="flashcards-reader-feedback" data-answer-result={practice.mark === 1 ? 'correct' : 'wrong'}
    className="text-on-surface-secondary text-sm">{practice.feedback}</span> : null
  const spokenAnswer = shown && spoken ? <span aria-label="Answer" data-testid="flashcards-reader-spoken-answer" className="text-sm">{describeTrainingAnswer(prompt)}</span> : null
  const recorder = !shown && spoken ? <AudioRecorder size="sm" variant="text" onRecorded={onRecorded} /> : null
  const buttons = shown || spoken ? [] : [
    ...actions.map(action => action === 'voice' ? controls?.voice ? <fieldset key={action} disabled={grading} className="contents border-0 p-0"
      data-testid="flashcards-reader-voice" data-primary={action === controls.primary}>
      <AudioRecorder size="sm" variant="text" onRecorded={onVoice} />
    </fieldset> : null : controls?.reveal ? <IconButton key={action} data-testid="flashcards-reader-show-answer" data-primary={action === controls.primary}
      size="sm" variant="text" semanticRole="secondary" disabled={grading}
      onMouseDown={event => { event.preventDefault() }} onClick={onReveal}><EyeIcon />{t('showAnswer')}</IconButton> : null),
    // As training's toolbar: with reveal/voice actions Submit appears once there is an answer; otherwise it waits disabled.
    singleChoice || controls && !hasAnswer && !grading ? null : <IconButton key="submit" data-testid="flashcards-reader-submit" size="sm" variant="text" disabled={!hasAnswer || grading} loading={grading}
      onMouseDown={event => { event.preventDefault() }} onClick={onSubmit}><SendIcon />{t('submit')}</IconButton>,
  ].filter(Boolean)
  const nextItem = shown && onNext ? <IconButton data-testid="flashcards-reader-next" size="sm" variant="text"
    onMouseDown={event => { event.preventDefault() }} onClick={onNext}><ChevronRightIcon />{t('next')}</IconButton> : null
  const place = position && position.count > 1 && position.index >= 0 ? <span data-testid="flashcards-reader-practice-position"
    className="text-on-surface-secondary text-sm tabular-nums">{position.index + 1}/{position.count}</span> : null
  const error = !shown && practice?.status === 'failed' ? <span role="alert" data-testid="flashcards-reader-error" className="text-error text-sm">
    {t('gradingFailed', { error: unknownErrorToError(practice.error).message })}</span> : null
  // No control, nothing rendered: an empty child would leave a blank gap (no empty gaps on a page).
  if (!feedback && !spokenAnswer && !recorder && buttons.length === 0 && !error && !nextItem && !place) return null
  // Inline, right after the question's last part (its blank, or the end of its card), so it reads as that question's.
  return <span contentEditable={false} data-testid="flashcards-reader-practice" data-qid={prompt.qid}
    // A spoken response's prompt is centred: its record control sits centred under it.
    className={`inline-flex flex-wrap items-center gap-1 align-middle ${spoken ? 'self-center justify-center' : ''}`}>
    {feedback}{spokenAnswer}{recorder}{buttons}{place}{nextItem}{error}
  </span>
}
